/**
 * TagsInput — `.tags-input` from the prototype (cms-redesign-mockup.html
 * ~4466–4500). A chip cluster with an inline `<input>` for typing new
 * tags.
 *
 *   <TagsInput
 *     value={['anxiety', 'sleep', 'beginner']}
 *     onChange={(next) => setField('tags', next)}
 *     placeholder="Type a tag and press enter…"
 *   />
 *
 * Behavior:
 *   • Enter (or `,` / Tab) commits the typed value as a new chip
 *   • Backspace on an empty input removes the last chip
 *   • Trimmed + deduped (case-insensitive) before commit
 *   • Click the × on a chip to remove it
 *   • Click anywhere inside the wrapper to focus the input (the
 *     prototype's `cursor: text` recipe — feels native)
 *
 * Visual recipe:
 *   • Wrapper: cream bg, sand border, rounded-md, padding 8, min-h 44
 *   • Focus-within: surface bg, teal-green border, 4px teal-soft ring
 *   • Chips: teal-green-soft bg + teal-green-d text, asymmetric padding
 *     so the × button can sit flush right; chip has a 18px × button
 *     filled teal-green that brightens to teal-green-dark on hover
 */
import React, { useRef, useState } from 'react';
import { cn } from '../cn';

export default function TagsInput({
  value,
  onChange,
  placeholder = 'Type a tag and press enter…',
  className,
  inputId,
}) {
  const tags = Array.isArray(value) ? value : [];
  const inputRef = useRef(null);
  const [draft, setDraft] = useState('');

  const focusInput = () => inputRef.current?.focus();

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    // Dedupe case-insensitively. Keeps the first-typed casing as the
    // canonical form — matches how the v1 TagsSection's autocomplete
    // dedupe works under the hood.
    const lower = trimmed.toLowerCase();
    if (tags.some((t) => String(t).toLowerCase() === lower)) {
      setDraft('');
      return;
    }
    onChange?.([...tags, trimmed]);
    setDraft('');
  };

  const removeTag = (idx) => {
    const next = tags.slice(0, idx).concat(tags.slice(idx + 1));
    onChange?.(next);
  };

  const handleKeyDown = (e) => {
    // Enter / comma / Tab: commit the draft into a chip. Suppress
    // the default Enter form-submit so the surrounding <form> doesn't
    // submit when the user just meant to add a tag.
    if (e.key === 'Enter' || e.key === ',' || (e.key === 'Tab' && draft.trim())) {
      e.preventDefault();
      commitDraft();
      return;
    }
    // Backspace on an empty draft removes the last chip — standard
    // multi-value-input pattern.
    if (e.key === 'Backspace' && !draft && tags.length > 0) {
      e.preventDefault();
      removeTag(tags.length - 1);
    }
  };

  return (
    <div
      onClick={focusInput}
      className={cn('flex flex-wrap', className)}
      style={{
        gap: 6,
        padding: 8,
        background: 'rgb(var(--color-cream))', /* cream */
        border: '1px solid rgb(var(--color-sand))', /* sand */
        borderRadius: 8,
        minHeight: 44,
        cursor: 'text',
        transition: 'all 160ms ease',
      }}
      onFocus={(e) => {
        e.currentTarget.style.background = 'rgb(var(--color-surface))'; /* surface */
        e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))'; /* teal-green */
        e.currentTarget.style.boxShadow = '0 0 0 4px #75A29333'; /* teal-soft */
      }}
      onBlur={(e) => {
        // Re-evaluate on blur — checks if focus moved outside the
        // wrapper entirely (using the relatedTarget) so we don't flip
        // back to the resting style while the user is still
        // interacting with the input or a chip's × button.
        if (e.currentTarget.contains(e.relatedTarget)) return;
        e.currentTarget.style.background = 'rgb(var(--color-cream))';
        e.currentTarget.style.borderColor = 'rgb(var(--color-sand))';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {tags.map((tag, idx) => (
        <span
          key={`${tag}-${idx}`}
          className="inline-flex items-center"
          style={{
            gap: 6,
            background: '#75A29333', /* teal-green-soft */
            color: '#5C7E72', /* teal-green-d */
            padding: '3px 4px 3px 11px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {tag}
          <button
            type="button"
            aria-label={`Remove tag ${tag}`}
            onClick={(e) => {
              e.stopPropagation();
              removeTag(idx);
            }}
            className="grid place-items-center cursor-pointer"
            style={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              background: 'rgb(var(--color-teal-green))', /* teal-green */
              color: '#FFFFFF',
              fontSize: 11,
              lineHeight: 1,
              border: 0,
              padding: 0,
              transition: 'background 160ms ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#5C7E72'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgb(var(--color-teal-green))'; }}
          >
            ×
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Commit on blur too — losing focus with a typed value but
          // no Enter shouldn't silently drop the tag.
          if (draft.trim()) commitDraft();
        }}
        placeholder={placeholder}
        style={{
          border: 0,
          background: 'transparent',
          outline: 'none',
          flex: 1,
          minWidth: 100,
          fontFamily: 'inherit',
          fontSize: 13,
          color: 'rgb(var(--color-navy))', /* navy */
          padding: '4px 6px',
        }}
      />
    </div>
  );
}
